import axios from 'axios'
import Cookie from 'js-cookie'
import storage from 'store'
import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import type { HttpInstance, ResponseParser, ScopedHeader } from './types'
import { buildAxiosConfigFromOptions } from './call'
import { makeTrackedController, releaseController, cancelAll } from './cancel'
import { STORAGE_KEYS } from '@/constants'
import { LOGIN_ROUTE } from '@/router/constants'
import { makeCodeResponseParser, axiosDataFallbackParser } from '@/libs/http/parsers'

const { ACCESS_TOKEN, TOKEN_TYPE, DEVICE_ID } = STORAGE_KEYS

function createAxiosHttp(
  config: AxiosRequestConfig,
  opts: { authDefault?: boolean; publicPrefixes?: string[] } = {},
): HttpInstance {
  const authDefault = opts.authDefault ?? true
  const publicPrefixes = opts.publicPrefixes ?? ['/api/pub', '/api/third']

  const _axios = axios.create(config) as AxiosInstance

  // 拦截器只注册一次；解析器以“可变数组”存放，setGlobalParser 仅更新此数组
  const __state = {
    parsers: [] as ResponseParser<any, any>[],

    // 作用域级临时头（通过 useScopedHeaders 注入，直到 clear() 或自动清除）
    scopedHeaders: [] as Array<ScopedHeader>,
  }

  /* ——— 业务增强方法：解析器设置 / Cookie 授权工具 ——— */
  ;(_axios as any).setGlobalParser = (parsers: ResponseParser<any, any>[]) => {
    __state.parsers = Array.isArray(parsers) ? parsers.slice() : []
  }
  ;(_axios as any).setParsers = (_axios as any).setGlobalParser
  ;(_axios as any).setAuthorization = (token: string, expires: number | Date, name?: string) => {
    Cookie.set(name ?? _axios.defaults.xsrfCookieName!, token, { expires })
  }
  ;(_axios as any).removeAuthorization = (name?: string) => {
    Cookie.remove(name ?? _axios.defaults.xsrfCookieName!)
  }
  ;(_axios as any).checkAuthorization = (name?: string) => {
    return Boolean(Cookie.get(name ?? _axios.defaults.xsrfCookieName!))
  }

  /* ——— 请求拦截器 ——— */
  _axios.interceptors.request.use((req) => {
    // 将外部 signal 与内部 AbortController 绑定，同时纳入全局取消池
    const abortController = makeTrackedController((req as any).signal as AbortSignal | undefined)
    ;(req as any).signal = abortController.signal
    ;(req as any).__abortController = abortController

    // 根据前缀/开关决定是否附加 Authorization；无 token 且需要鉴权则中断
    const url = req.url || ''
    const isPublicByPrefix = publicPrefixes.some((p) => url.startsWith(p))
    const explicitAuth =
      typeof (req as any).useAuth === 'boolean' ? (req as any).useAuth : undefined
    const needAuth = explicitAuth ?? (authDefault && !isPublicByPrefix)
    const accToken = storage.get(ACCESS_TOKEN)
    const tokenType = storage.get(TOKEN_TYPE) || 'Bearer'
    if (!needAuth) {
      // 明确不需要鉴权时，移除已合并进来的默认 Authorization 头，避免误带 token
      const headers = (req.headers || {}) as Record<string, any>
      delete headers.Authorization
      delete headers.authorization
      // 保留其他默认请求头（如语言相关）
      req.headers = { ...req.headers, ...headers }
    } else {
      if (!accToken) {
        // 需要鉴权但没有 token，直接中断
        abortController.abort()
        return Promise.reject(new Error('UNAUTHENTICATED'))
      }
      // 需要鉴权且有 token，注入 Authorization
      req.headers = { ...req.headers, Authorization: `${tokenType} ${accToken}` }
    }
    req.headers = { ...req.headers, 'device-id': Date.now().toString() }
    if (storage.get(DEVICE_ID)) {
      // req.headers = { ...req.headers, 'device-id': storage.get(DEVICE_ID) }
    }

    // 作用域级临时头合并（支持可选 match 函数）
    if (__state.scopedHeaders.length) {
      for (const scopedHeader of __state.scopedHeaders) {
        req.headers = { ...(req.headers || {}), ...scopedHeader.headers }
        scopedHeader.inflight = (scopedHeader.inflight || 0) + 1
      }
    }

    // 单笔请求级临时头（配置里的 tempHeaders，优先级最高）
    const onceHeaders = (req as any).__onceHeaders
    const tempHeaders = (req as any).tempHeaders
    if (tempHeaders && typeof tempHeaders === 'object') {
      req.headers = { ...(req.headers || {}), ...tempHeaders }
      delete (req as any).tempHeaders // 避免 axios 重试场景复用
    }
    if (onceHeaders && typeof onceHeaders === 'object') {
      req.headers = { ...(req.headers || {}), ...onceHeaders }
      delete (req as any).__onceHeaders // 避免复用
    }

    return req
  })

  _axios.interceptors.response.use(
    async (res) => {
      // 请求完成后释放对应的 AbortController，避免泄漏
      releaseController((res.config as any).__abortController as AbortController | undefined)

      // 自动清除"命中且要求响应后自动清除"的作用域临时头
      if (__state.scopedHeaders.length) {
        for (const sh of __state.scopedHeaders) {
          if (sh.inflight && sh.inflight > 0) sh.inflight -= 1
        }
        __state.scopedHeaders = __state.scopedHeaders.filter((sh) => {
          return !(sh.autoClearAfterResponse && (sh.inflight || 0) === 0)
        })
      }

      // 依次尝试解析器；返回 undefined 表示跳过，继续传递原响应
      for (const p of __state.parsers) {
        try {
          const r = p(res)
          if (r !== undefined) return r
        } catch (err) {
          throw err
        }
      }

      // 没有解析器命中 → 透传原始响应
      return res
    },
    async (err) => {
      // 401 统一处理——清 token、跳登录（避免在登录页重复跳转）
      if (err?.response?.status === 401) {
        storage.remove(ACCESS_TOKEN)
        storage.remove(TOKEN_TYPE)

        if (window.location.pathname !== LOGIN_ROUTE.path) {
          const { pathname, search, hash } = window.location
          const redirect = encodeURIComponent(`${pathname}${search}${hash}`)
          const joiner = LOGIN_ROUTE.path.includes('?') ? '&' : '?'
          const redirectUrl = `${LOGIN_ROUTE.path}${joiner}redirect=${redirect}`
          console.log('redirectUrl', redirectUrl)
          window.location.replace(redirectUrl)
        }

        /* if (window.location.pathname !== LOGIN_ROUTE.path) {
          window.location.replace(LOGIN_ROUTE.path)
        } */
      }

      // 错误同样需要清除命中作用域头（与成功分支一致）
      if (__state.scopedHeaders.length) {
        for (const sh of __state.scopedHeaders) {
          if (sh.inflight && sh.inflight > 0) sh.inflight -= 1
        }
        __state.scopedHeaders = __state.scopedHeaders.filter((sh) => {
          return !(sh.autoClearAfterResponse && (sh.inflight || 0) === 0)
        })
      }
      // codeResponseParser 里可能抛出 'UNAUTHENTICATED'（无 response）
      if (err?.message === 'UNAUTHENTICATED') {
        releaseController((err?.config as any)?.__abortController as AbortController | undefined)
        return Promise.reject(err)
      }

      // 将带 response 的错误标准化；无 response 的视为网络层错误
      if (err?.response) {
        const e: any = new Error(err.response.statusText)
        e.status = err.response.status
        e.code = err.response.data?.code
        e.data = err.response.data
        releaseController((err?.config as any)?.__abortController as AbortController | undefined)
        return Promise.reject(e)
      }

      const e: any = new Error(err?.message || 'Network Error')
      e.isNetworkError = true
      releaseController((err?.config as any)?.__abortController as AbortController | undefined)
      return Promise.reject(e)
    },
  )

  // 实例级取消（退出登录等场景集中取消所有未决请求）
  ;(_axios as any).cancelAll = cancelAll

  // 实例级设置/移除鉴权头，并与本地存储联动
  ;(_axios as any).setAuthToken = (token?: string | null, type = 'Bearer') => {
    if (token) {
      _axios.defaults.headers.common.Authorization = `${type} ${token}`
    } else {
      delete _axios.defaults.headers.common.Authorization
    }
  }

  /** ================== 单次 / 作用域 临时头 API ================== */

  // 仅“下一次请求”临时加头；该请求完成后自动清除；返回 clear() 可提前手动清除
  ;(_axios as any).useOnceHeader = (headers: Record<string, string>) => {
    let consumed = false
    const id = _axios.interceptors.request.use((req) => {
      if (!consumed) {
        ;(req as any).__onceHeaders = headers
        consumed = true
        // 立刻注销，避免并发期间多请求同时命中
        _axios.interceptors.request.eject(id)
      }
      return req
    })
    // 手动清除：若还未被消费，则提前注销
    const clear = () => {
      _axios.interceptors.request.eject(id)
    }
    return { id, clear }
  }

  // 在“作用域内”对所有请求加头；返回 clear()；可选命中后自动清除（autoClearAfterResponse + match）
  ;(_axios as any).useScopedHeaders = (
    headers: Record<string, string>,
    opts?: {
      /** 命中一次（成功/失败）后自动清除 */
      autoClearAfterResponse?: boolean
    },
  ) => {
    const item = {
      id: Symbol('SCOPED_HEADERS'),
      headers,
      autoClearAfterResponse: opts?.autoClearAfterResponse ?? false,
    }
    __state.scopedHeaders.push(item)
    const clear = () => {
      __state.scopedHeaders = __state.scopedHeaders.filter((it) => it.id !== item.id)
    }
    return { id: item.id, clear }
  }
  ;(_axios as any).removeScopedHeadersById = (id: symbol) => {
    __state.scopedHeaders = __state.scopedHeaders.filter((it) => it.id !== id)
  }

  // 新增对象式入口 http.call，不覆盖 axios.request，零破坏兼容 get/post
  ;(_axios as any).call = async function <T = any>(
    options: Parameters<typeof buildAxiosConfigFromOptions>[0],
  ): Promise<T> {
    const cfg = buildAxiosConfigFromOptions(options)
    return _axios.request(cfg) as Promise<T>
  }

  return _axios as unknown as HttpInstance
}

// —— 创建实例 —— //
const http = createAxiosHttp(
  {
    timeout: 60000,
    baseURL: import.meta.env.VITE_API_URL as string,
    withCredentials: true,
    xsrfCookieName: 'Authorization',
    xsrfHeaderName: 'Authorization',
  },
  { authDefault: true, publicPrefixes: ['/api/pub', '/api/third'] },
)

;(http as any).setGlobalParser([makeCodeResponseParser(), axiosDataFallbackParser])

export default http
export { cancelAll }
